import { computed, ref, watch } from "vue";
import showConfirm from "@/components/utils/ui_v4/alerts/confirm/confirm";
import api from "@/api/api";
import alturaDaJanela from "@/helpers/ResponsiveHeightReativo";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";

export default function useCotacaoDialog(props) {
  const dialog = ref(false);
  const openOpenCotacao = ref({ isOpen: false, idCotacao: null });
  const openPercentTolerancia = ref({ isOpen: false });
  const isLoading = ref(false);
  const selected = ref([]);
  const dataSet = ref([]);
  const fornecedores = ref([]);
  const message = ref(null);
  const errors = ref(null);

  const usersId = ref(localStorage.getItem("id"));

  const alturaTabela = computed(() => alturaDaJanela.value - 215);

  const headers = [
    { text: "EAN/GTIN", value: "ean_gtin", sortable: true, tooltip: "Código de barras do produto" },
    { text: "SKU Systock", value: "sku_systock", tooltip: "Código do produto no Systock." },
    { text: "Descrição", value: "descricao", tooltip: "Nome claro do item. Sempre obrigatório." },
    { text: "Preço referência", value: "preco_referencia", tooltip: "Preço do produto cadastrado no Systock para referência. Este preço serve de apoio para você, ele não é enviado ao fornecedor." },
    { text: "Qtd. mín aceitável", value: "min_aceitavel", tooltip: "Limite mínimo que o comprador aceita receber (na U.M. mínima)." },
    { text: "Qtd. pedida", value: "qtd_pedida", tooltip: "Quantidade que o comprador <strong>quer</strong> comprar, na U.M. mínima." },
    { text: "Qtd. máx aceitável", value: "max_aceitavel", tooltip: "Limite máximo que o comprador aceita receber (na U.M. mínima)." },
    { text: "U.M. mínima", value: "um_minima", tooltip: "Unidade mínima de compra usada como base da cotação (ex.: UN, KG, M)." },
    { text: "Qtd. pedido na U.M. master", value: "qtd_pedida_na_um_master", tooltip: "Quantidade pedida equivalente na embalagem master (CX/FARDO etc.)." },
    { text: "U.M. master", value: "um_master", tooltip: "Embalagem maior (ex.: CX, FARDO, PALLET). Opcional. Ao trocar a Unidade de Medida, defina o <strong>Fator de conversão</strong>." },
    { text: "Fator de conversão (mínima-master)", value: "fator_de_conversao_minima_master", tooltip: "Quantas U.M. mínima cabem em 1 U.M. master (ex.: 12, 6, 2.5)." },
    {
      text: "Editar",
      value: "acoes",
      style: {
        backgroundColor: 'white',
        position: 'sticky !important',
        zIndex: '1 !important',
        minWidth: '80px !important',
        maxWidth: '80px !important',
        right: 0,
        left: 'auto',
        borderLeft: '2px solid rgba(0,0,0,.1)',
        cursor: 'pointer',
      }
    }
  ];

  const fieldLabels = {
    idpedido: "Número do Pedido",
    ean_gtin: "EAN/GTIN",
    sku_systock: "SKU do Systock",
    sku_fornecedor: "SKU do Fornecedor",
    descricao: "Descrição do Produto",
    qtd_minima_aceita_Emb: "Quantidade Mínima Aceita",
    qtd_maxima_aceita_Emb: "Quantidade Máxima Aceita",
    qtd_pedida: "Quantidade Pedida",
    um_minima: "Unidade Mínima",
    qtd_pedida_na_um_master: "Qtd. na UM Master",
    um_master: "UM Master",
    fator_de_conversao_minima_master: "Fator de Conversão",
    preco_referencia: "Preço de Referência",
    idfornecedor: "Fornecedor",
    razao_social: "Razão Social",
    email: "E-mail",
    telefone: "Telefone",
  };

  const getData = async () => {

    isLoading.value = true;
    try {
      const { data: response } = await api.get(`/cotacao/listar_produtos_cotacao_carrinho_systock?idpedido=${props.pedidoId}`);

      dataSet.value = applyDefaultTolerancia(response.produtos || []);
    } catch (error) {
      void error;
    } finally {
      isLoading.value = false;
    }
  }

  const getFornecedores = async () => {
    isLoading.value = true;
    try {
      const { data: response } = await api.get(`/cotacao/listar_fornecedores_cotacao_carrinho_systock?idpedido=${props.pedidoId}`);

      fornecedores.value = response.fornecedores;
    } catch (error) {
      void error;
    } finally {
      isLoading.value = false;
    }
  }

  const formatBRL = (valor) => {
    if (valor == null) return ''
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(valor)
  }

  const applyDefaultTolerancia = (produtos) => {
    return produtos.map((item) => {
      const qtd = Number(item.qtd_pedida) || 0;
      const minMissing = item.min_aceitavel === null || item.min_aceitavel === undefined || item.min_aceitavel === '';
      const maxMissing = item.max_aceitavel === null || item.max_aceitavel === undefined || item.max_aceitavel === '';

      if (!minMissing && !maxMissing) {
        return item;
      }

      const minCalc = qtd - Math.floor((qtd * 10) / 100);
      const maxCalc = qtd + Math.ceil((qtd * 10) / 100);

      return {
        ...item,
        min_aceitavel: minMissing ? minCalc : item.min_aceitavel,
        max_aceitavel: maxMissing ? maxCalc : item.max_aceitavel,
      };
    });
  };

  const handleExcluir = (item) => {
    showConfirm({
      title: 'Remover produto',
      message: 'Tem certeza que deseja remover o produto?',
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: (confirmed) => {
        if (confirmed) {
          dataSet.value = dataSet.value.filter(
            p => p.sku_systock !== item.sku_systock
          );

          //  Se ele tiver selecionado, removemos do selected também
          selected.value = selected.value.filter(
            p => p.sku_systock !== item.sku_systock
          );

          toast.success('Item removido com sucesso!');
        }
      }
    });
  };

  const handleExcluirSelecionados = () => {
    if (!selected.value || selected.value.length === 0) return;

    showConfirm({
      title: 'Remover produtos selecionados',
      message: `Tem certeza que deseja remover ${selected.value.length} produto(s) selecionado(s)?`,
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: (confirmed) => {
        if (confirmed) {
          const selectedSkus = new Set(selected.value.map(item => item.sku_systock));

          dataSet.value = dataSet.value.filter(
            p => !selectedSkus.has(p.sku_systock)
          );

          selected.value = [];

          toast.success('Itens selecionados removidos com sucesso!');
        }
      }
    });
  };

  const isAllSelected = computed(() => {
    return selected.value.length <= 0;
  })

  // Função auxiliar para limitar casas decimais
  const limitDecimals = (value, decimals = 6) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (isNaN(num)) return 0;

    // Se for inteiro, retorna inteiro
    if (Number.isInteger(num)) return num;

    // Arredonda para o número de casas decimais especificado
    return parseFloat(num.toFixed(decimals));
  };

  const handleNovaCotacao = async () => {
    try {
      const payload = {
        user_id: usersId.value,

        produtos: dataSet.value.map(item => ({
          idpedido: item.idpedido,
          ean_gtin: item.ean_gtin || "0",
          sku_systock: item.sku_systock,
          sku_fornecedor: item.sku_fornecedor,
          descricao: item.descricao,
          qtd_minima_aceita_Emb: limitDecimals(item.min_aceitavel),
          qtd_pedida: limitDecimals(item.qtd_pedida),
          qtd_maxima_aceita_Emb: limitDecimals(item.max_aceitavel),
          um_minima: item.um_minima,
          qtd_pedida_na_um_master: limitDecimals(item.qtd_pedida_na_um_master),
          um_master: item.um_master,
          fator_de_conversao_minima_master: item.fator_de_conversao_minima_master,
          preco_referencia: item.preco_referencia,
        })),

        fornecedores: fornecedores.value.map(f => ({
          idpedido: f.idpedido,
          idfornecedor: f.idfornecedor,
          razao_social: f.razao_social,
          cnpj: f.cnpj || null,
          email: f.email || null,
          telefone: f.telefone_celular || null,
        }))
      };

      const { data: response } = await api.post("/cotacao/importar_criar_cotacao", payload);

      await handleOpenCotacao(response.cotacao_transacao_id);

      toast.success("Cotação criada com sucesso!");

    } catch (error) {
      message.value = error.response.data.message || "Erro ao criar cotação";

      const apiErrors = error.response.data.errors || [];

      errors.value = apiErrors.map(field => fieldLabels[field] || field);

      toast.error(message.value);
      toast.error("Alguns campos estão faltando, corrija e tente novamente.");
    }
  };

  const handleOpenCotacao = (id) => {
    openOpenCotacao.value.isOpen = true;
    openOpenCotacao.value.idCotacao = id;
  };

  const abrirDialog = () => {
    dialog.value = true;
  };

  const fecharDialog = () => {
    dialog.value = false;
  };

  const loadTolerancia = async (data) => {
    const { minima, maxima, applyAll } = data;

    const selectedKeys = new Set(selected.value.map(item => item.sku_systock));

    dataSet.value = dataSet.value.map(item => {
      if (!applyAll && !selectedKeys.has(item.sku_systock)) {
        return item;
      }

      const qtd = Number(item.qtd_pedida) || 0;
      const minPercent = minima || 0;
      const maxPercent = maxima || 0;

      const minCalc = minPercent === 0 ? 0 : qtd - Math.floor((qtd * minPercent) / 100);
      const maxCalc = maxPercent === 0 ? 0 : qtd + Math.ceil((qtd * maxPercent) / 100);

      return {
        ...item,
        min_aceitavel: minCalc,
        max_aceitavel: maxCalc,
      };
    });

    const minPercent = minima || 0;
    const maxPercent = maxima || 0;
    const count = applyAll ? dataSet.value.length : selectedKeys.size;

    if (minPercent === 0 && maxPercent === 0) {
      const msg = applyAll
        ? `Tolerância zerada para ${count} itens da cotação.`
        : `Tolerância zerada para ${count} itens selecionados.`;
      toast.success(msg);
    } else {
      const msg = applyAll
        ? `Tolerância aplicada para ${count} itens da cotação.`
        : `Tolerância aplicada para ${count} itens selecionados.`;
      toast.success(msg);
    }
  }

  const handleClear = () => {
    showConfirm({
      title: 'Limpar lista',
      message: 'Tem certeza que deseja limpar a lista e remover os dados?',
      button: {
        yes: 'Limpar',
        no: 'Cancelar'
      },
      callback: (confirmed) => {
        if (confirmed) {
          // Limpar dados em memória
          dataSet.value = [];
          selected.value = [];
          fornecedores.value = [];
          toast.success('Todos os itens da lista foram excluídos.');
        }
      }
    });
  };

  const resetData = async () => {
    // Recarregar todos os dados
    await getData();
    await getFornecedores();
    toast.success('Dados recarregados com sucesso!');
  };

  const handlePercentTolerancia = () => {
    openPercentTolerancia.value.isOpen = true;
  };

  watch(() => dialog.value, async (newValue) => {
    if (newValue) {
      await getData();
      await getFornecedores();
    }
  }, { immediate: true });

  return {
    dialog,
    openOpenCotacao,
    openPercentTolerancia,
    abrirDialog,
    fecharDialog,
    loadTolerancia,
    selected,
    handleClear,
    handleExcluirSelecionados,
    handlePercentTolerancia,
    handleNovaCotacao,
    isLoading,
    headers,
    alturaTabela,
    handleExcluir,
    dataSet,
    isAllSelected,
    formatBRL,
    resetData,
  };
}
